<script setup lang="ts">
    // @ts-nocheck

    /**
     * Filtrering av events i kalendern. schema används för att generera formuläret
     */

    import KForm from "@/components/forms/Form/KForm.vue";
    import { nextTick, ref, watch } from "vue";

    defineProps<{
        // Form schema
        schema?: unknown;
        initialValuesGetter?: () => Promise<Partial<unknown> | null>;
        triggers?: Array<{
            fieldName: string; // Namn på fältet
            dependentOn: string; // Namn på fältet som ska lyssnas på
            action: ({ value, dependentOnValue }: { value: unknown; dependentOnValue: unknown }) => {
                value?: unknown;
                disabled?: boolean;
                setAsInitial?: boolean;
            }; // Funktion som tar emot båda fältens värde och returnerar nytt värde och/eller om fältet ska bli disabled
        }>;
    }>();

    const emit = defineEmits<{
        (e: "submit", formData: { [key: string]: unknown }, node: unknown): void;
    }>();

    const visible = ref(false);
    const formRef = ref();
    const drawerRef = ref();

    const sectionPadding = "1.5rem";

    /**
     * Toggla visibility på drawer
     * @param overrideVisible boolean som sätts som nya värdet istället för toggle av visible
     */
    const toggleVisibility = async ({ overrideVisible }: { overrideVisible?: boolean } = {}) => {
        // Ta bort lyssnare på sidebar mask, om visible kommer togglas eller sättas till false
        if (
            (typeof overrideVisible === "boolean" && !overrideVisible) ||
            (typeof overrideVisible !== "boolean" && visible.value)
        ) {
            const maskElement = document.getElementsByClassName(
                "p-sidebar-mask p-component-overlay p-component-overlay-enter"
            )?.[0];

            if (maskElement) {
                maskElement.removeEventListener("click", onClickOutside);
            }
        }

        if (typeof overrideVisible === "boolean") {
            visible.value = overrideVisible;
        } else {
            visible.value = !visible.value;
        }
    };

    defineExpose({
        toggleVisibility,
    });

    const onSubmit = (data: { [key: string]: unknown }, node: unknown) => {
        emit("submit", data, node);
        visible.value = false;
    };

    const onCancel = () => {
        visible.value = false;
    };

    /**
     * Callback för blick på sidebar mask, vilket är utanför drawer
     */
    const onClickOutside = async () => {
        const filterData = await formRef?.value?.manualSubmit();
        emit("submit", filterData, null);
        visible.value = false;
    };

    watch(visible, async (newVisible) => {
        // Hitta och sitt click listener på sidebar mask om drawer är synlig
        if (newVisible) {
            // Behöver vänta på att sidebar mask har hunnit rendera
            await nextTick();

            const maskElement = document.getElementsByClassName(
                "p-sidebar-mask p-component-overlay p-component-overlay-enter"
            )?.[0];

            if (maskElement) {
                maskElement.addEventListener("click", onClickOutside);
            }
        }
    });
</script>

<template>
    <!--
        @update:visible emittas när PrimeDrawer försöker göra ändringar på visible internt. I det här fallet
        använder vi det när close button klickas på
    -->
    <PrimeDrawer
        ref="drawerRef"
        :visible="visible"
        :dismissable="false"
        position="right"
        class="calendar-filter"
        @update:visible="toggleVisibility({ overrideVisible: false })"
    >
        <template #header>
            <span>Filtrering</span>
        </template>
        <KForm
            ref="formRef"
            id="calendarFilter"
            :schema="schema"
            submitContent="Filtrera"
            cancelContent="Avbryt"
            @submit="onSubmit"
            @cancel="onCancel"
            :initialValuesGetter="initialValuesGetter"
            :triggers="triggers"
            :sectionPadding="sectionPadding"
            submitSize="secondary"
            cancelSize="slim"
        >
            <slot />
        </KForm>
    </PrimeDrawer>
</template>

<style lang="scss" scoped>
    @import "@/style/form";
    .calendar-filter {
        width: 22rem;
        max-width: 100%;
        // Går av någon anledning inte att göra v-bind på sectionPadding. Ändra den och här om du ska ändra padding
        $section-padding: 1.5rem;

        ::v-deep .p-sidebar-header {
            padding: 0.5rem $section-padding;
            border-bottom: 1px solid $form-section-divider-color;
            margin-bottom: $form-row-gap;

            .p-sidebar-header-content {
                font-weight: bold;
                width: 100%;
            }
        }

        ::v-deep .p-sidebar-content {
            padding: 0;
        }
    }
</style>
